import AddIcon from "@mui/icons-material/Add";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import {
  OUTIL_COLORS,
  paperSx,
  tableHeaderSx,
} from "./OutilContainer.styles";

const SEARCH_FIELDS = [
  "equipment",
  "owner",
  "commissioningDate",
  "reference",
  "brand",
  "supplier",
  "serialNumber",
  "assignment",
  "plannedDate",
  "actualDate",
  "observations",
];

const COLUMNS = [
  ["Matériel", "equipment", 170],
  ["Propriétaire", "owner", 150],
  ["Mise en service", "commissioningDate", 145, "date"],
  ["N° Réf.", "reference", 130],
  ["Marque", "brand", 130],
  ["Fournisseur", "supplier", 150],
  ["N° Série", "serialNumber", 145],
  ["Affectation", "assignment", 155],
  ["Date prévue MP", "plannedDate", 155, "date"],
  ["Date réelle MP", "actualDate", 155, "date"],
  ["Observations", "observations", 180],
];

const compactFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": {
    backgroundColor: "#fff",
    borderRadius: "5px",
    fontSize: 10,
    minHeight: 32,
  },
  "& .MuiInputBase-input": { px: 0.8, py: 0.65 },
};

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isLate = (row) =>
  Boolean(row.plannedDate) &&
  !row.actualDate &&
  /^\d{4}-\d{2}-\d{2}$/.test(row.plannedDate) &&
  row.plannedDate < localDateKey();

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const createEquipment = () => ({
  actualDate: "",
  assignment: "",
  brand: "",
  commissioningDate: "",
  equipment: "",
  id: `maintenance-${Date.now()}`,
  observations: "",
  owner: "",
  plannedDate: "",
  reference: "",
  serialNumber: "",
  supplier: "",
});

const MaintenancePreventive = ({ onChange, value }) => {
  const [search, setSearch] = useState("");
  const rows = useMemo(
    () => (Array.isArray(value.maintenanceRows) ? value.maintenanceRows : []),
    [value.maintenanceRows]
  );

  const filteredRows = useMemo(() => {
    const query = normalizeText(search).trim();
    if (!query) return rows;
    return rows.filter((row) =>
      SEARCH_FIELDS.some((field) => normalizeText(row[field]).includes(query))
    );
  }, [rows, search]);

  const setRows = (nextRows) => onChange("maintenanceRows", nextRows);
  const addRow = () => setRows([...rows, createEquipment()]);
  const deleteRow = (rowId) => setRows(rows.filter((row) => row.id !== rowId));
  const updateRow = (rowId, field, nextValue) => {
    setRows(rows.map((row) => (row.id === rowId ? { ...row, [field]: nextValue } : row)));
  };

  return (
    <Paper elevation={0} role="region" aria-label="Maintenance préventive" sx={{ ...paperSx, overflow: "hidden" }}>
      <Box
        sx={{
          alignItems: "center",
          backgroundColor: OUTIL_COLORS.panelHeader,
          borderBottom: `1px solid ${OUTIL_COLORS.border}`,
          display: "flex",
          justifyContent: "space-between",
          minHeight: 48,
          px: 1.5,
        }}
      >
        <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
          <BuildCircleOutlinedIcon sx={{ fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
          Maintenance préventive — §7.1.3
        </Typography>
        <Button
          onClick={addRow}
          size="small"
          startIcon={<AddIcon />}
          sx={{ backgroundColor: "#d5ad3f", color: "#10263d", fontSize: 10, fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#c79c2f" } }}
          variant="contained"
        >
          Ajouter
        </Button>
      </Box>

      <Box sx={{ alignItems: "center", backgroundColor: "#f5f8fb", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "6px", display: "flex", gap: 0.7, mx: 1.5, my: 1, px: 1.2, py: 0.8 }}>
        <LightbulbOutlinedIcon sx={{ color: "#d5ad3f", fontSize: 16 }} />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
          Cliquez sur Ajouter pour enregistrer localement un équipement.
        </Typography>
        <WarningAmberIcon sx={{ color: "#d97706", fontSize: 15, ml: 0.5 }} />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
          signale une maintenance en retard.
        </Typography>
      </Box>

      <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
        <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
        <TextField
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Filtrer toutes les colonnes..."
          size="small"
          sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
          value={search}
        />
        <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} entrée(s)</Typography>
        <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table aria-label="Registre de maintenance préventive" size="small" sx={{ minWidth: 1750, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(([label, field, width]) => <TableCell key={field} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}
              <TableCell sx={{ ...tableHeaderSx, width: 85 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow><TableCell align="center" colSpan={12} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucun équipement enregistré.</TableCell></TableRow>
            ) : filteredRows.map((row) => {
              const late = isLate(row);
              return (
                <TableRow key={row.id} sx={{ backgroundColor: late ? "#fff7ed" : "#fff", "&:nth-of-type(even)": { backgroundColor: late ? "#fff2e2" : "#f7f9fc" } }}>
                  {COLUMNS.map(([label, field, , type]) => (
                    <TableCell key={field} sx={{ position: field === "plannedDate" ? "relative" : "static" }}>
                      <TextField
                        inputProps={{ "aria-label": label }}
                        onChange={(event) => updateRow(row.id, field, event.target.value)}
                        placeholder={type === "date" ? undefined : `${label}...`}
                        size="small"
                        sx={compactFieldSx}
                        type={type || "text"}
                        value={row[field] || ""}
                      />
                      {field === "plannedDate" && late && (
                        <Tooltip title="Maintenance en retard">
                          <WarningAmberIcon sx={{ color: "#d97706", fontSize: 15, position: "absolute", right: 8, top: 2 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Tooltip title="Supprimer">
                      <IconButton aria-label={`Supprimer ${row.equipment || "l'équipement"}`} onClick={() => deleteRow(row.id)} size="small" sx={{ backgroundColor: "#b62424", borderRadius: "6px", color: "#fff", "&:hover": { backgroundColor: "#941b1b" } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MaintenancePreventive;
